import React, { useEffect } from "react";
import {
  Paper,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Badge,
  Box,
  Flex,
} from "@mantine/core";
import {
  IconArrowRight,
  IconAt,
  IconBolt,
  IconCurrencyBitcoin,
  IconDeviceMobile,
  IconPlugConnected,
} from "@tabler/icons-react";
import { getPitchStrings } from "./pitchStrings";
import { trackGoal } from "../helpers/trackGoal";

const ORANGE = "#f7931a";
// The first tool page seen in this tab. layouts/_plausible_signup.html.erb sends it
// with the "Signup" goal, so sign-ups can be traced back to a tool page.
export const TOOL_PAGE_KEY = "rz_tool_page";

const rememberToolPage = () => {
  try {
    if (!window.sessionStorage.getItem(TOOL_PAGE_KEY)) {
      window.sessionStorage.setItem(TOOL_PAGE_KEY, window.location.pathname);
    }
  } catch (_e) {
    // storage blocked: the Signup goal just goes without a tool page
  }
};

const ctaClicked = () => trackGoal("Tool CTA clicked", { page: window.location.pathname });
const FEATURE_ICONS = [
  IconAt,
  IconCurrencyBitcoin,
  IconBolt,
  IconPlugConnected,
  IconDeviceMobile,
];

interface Props {
  locale?: string;
  /** One sentence tying the pitch to the tool it sits under (English pages only). */
  lead?: string;
  /** Replace the subhead / feature list, e.g. on /bitcoin-fees, where "no fees to receive"
   *  and "settle in about a second" would read as a Lightning vs on-chain fee comparison. */
  subhead?: string;
  features?: string[];
}

/** Compact "what you get with Rizful" pitch for the bottom of public utility pages. */
const RizfulPitch: React.FC<Props> = ({ locale, lead, subhead, features }) => {
  const strings = getPitchStrings(locale);
  const s = {
    ...strings,
    subhead: subhead ?? strings.subhead,
    features: features ?? strings.features,
  };
  useEffect(rememberToolPage, []);

  return (
    <Paper
      mt="xl"
      mb="xl"
      p={{ base: "md", sm: "lg" }}
      radius="md"
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(247, 147, 26, 0.25)",
      }}
    >
      <Flex
        direction={{ base: "column", sm: "row" }}
        gap="lg"
        align={{ base: "stretch", sm: "center" }}
      >
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <Group gap="sm" wrap="nowrap">
            <img
              src="https://daxwktpz4hkwd.cloudfront.net/logos/rizful-logo-square-192-not-maskable.png"
              alt="Rizful"
              width={44}
              height={44}
              style={{ borderRadius: 10, flexShrink: 0 }}
            />
            <Badge
              color="orange"
              variant="filled"
              size="lg"
              radius="sm"
              style={{ letterSpacing: "0.06em" }}
            >
              100% {s.free}
            </Badge>
          </Group>

          <Box>
            {lead && (
              <Text size="sm" c="gray.3" mb={6}>
                {lead}
              </Text>
            )}
            <Title order={3} c="white" style={{ lineHeight: 1.25 }}>
              {s.headline}
            </Title>
            <Text size="sm" c="dimmed" mt={4}>
              {s.subhead}
            </Text>
          </Box>

          <Stack gap={6}>
            {s.features.map((feature, i) => {
              const Icon = FEATURE_ICONS[i] ?? IconBolt;
              return (
                <Group key={i} gap={8} wrap="nowrap" align="center">
                  <Icon size={16} color={ORANGE} style={{ flexShrink: 0 }} />
                  <Text size="sm" c="gray.3">
                    {feature}
                  </Text>
                </Group>
              );
            })}
          </Stack>

          <Box mt="xs">
            <Button
              component="a"
              href="/"
              onClick={ctaClicked}
              color="orange"
              size="md"
              rightSection={<IconArrowRight size={18} />}
              styles={{
                root: { height: "auto", minHeight: 42, paddingBlock: 8 },
                label: { whiteSpace: "normal", textAlign: "center" },
              }}
            >
              {s.cta}
            </Button>
          </Box>
        </Stack>

        <a
          href="/"
          onClick={ctaClicked}
          style={{ display: "flex", justifyContent: "center", flexShrink: 0 }}
        >
          <img
            src="https://daxwktpz4hkwd.cloudfront.net/home_page_images/rizful-interface-aug-4-2025.jpg"
            alt="Rizful Lightning homebase interface"
            loading="lazy"
            style={{
              width: 220,
              maxWidth: "100%",
              height: "auto",
              borderRadius: 14,
              border: "1px solid rgba(255, 255, 255, 0.12)",
              boxShadow: "0 8px 28px rgba(0, 0, 0, 0.45)",
            }}
          />
        </a>
      </Flex>
    </Paper>
  );
};

export default RizfulPitch;
